import styles from './Invoices.module.scss'
import InvoicesStatusBanner from './status-banner/InvoicesStatusBanner.tsx'
import { useStoreSelector } from 'hooks/store.ts'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { routes } from 'types/routes.ts'
import { UserTypes } from 'store/auth/types.ts'
import FeedbackWidget from 'components/feedback-widget/FeedbackWidget.tsx'
import useOpenFeedbackWidget from 'hooks/useOpenFeedbackWidget.ts'
import { WidgetSlug } from 'types/feedbackWidget.ts'
import { useEffect, useMemo, useRef, useState } from 'react'
import useInvoicesTable from 'pages/app/billing/invoices/table-data/useInvoicesTable.tsx'
import { InvoiceTableTabs } from 'pages/app/billing/invoices/table-data/types.ts'
import NoInvoices from './no-invoices/NoInvoices.tsx'
import InvoicesTable from './invoices-table/InvoicesTable.tsx'
import InvoiceEntryModal from './invoice-entry-modal/InvoiceEntryModal.tsx'
import TableLoadingSkeleton from './table-loading-skeleton/TableLoadingSkeleton.tsx'
import { Banner, Card, Tab, Toggle } from '@boltenergy-be/design-system'
import Link from 'components/link/Link.tsx'
import AddressSwitcher from 'components/address-switcher/AdressSwitcher.tsx'
import useWindowSize from 'hooks/useWindowSize.tsx'
import classNames from 'classnames'
import { PaginationProps } from '@boltenergy-be/design-system/dist/components/pagination/types'
import { selectIsProducerAccount } from 'store/contact/slice.ts'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'

const Invoices = () => {
  // i18n
  const { t } = useTranslation('invoices')

  // Feedback widget
  const { feedbackWidgetData, openFeedbackWidget } = useOpenFeedbackWidget({
    delay: 10,
    slug: WidgetSlug.INVOICES
  })

  // Redux State
  const { userType } = useStoreSelector((store) => store.auth)
  const { selected, accounts } = useStoreSelector((store) => store.contact)
  const isProducer = useStoreSelector(selectIsProducerAccount)
  const isSuperUser = userType === UserTypes.SUPER_USER

  // Access rights
  const billingCyclesAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.billingCycles)
  const instalmentAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.instalment)

  // React router
  const { search } = useLocation()
  const query = useMemo<URLSearchParams>(() => new URLSearchParams(search), [search])
  const tabFromQuery =
    query.get('tab') && Object.values(InvoiceTableTabs).includes(query.get('tab') as InvoiceTableTabs)
      ? (query.get('tab') as InvoiceTableTabs)
      : undefined

  // Window size
  const { isTablet } = useWindowSize()

  // Local state
  const [tabs, setTabs] = useState<Tab[]>([])
  const [activeTab, setActiveTab] = useState<InvoiceTableTabs>(
    tabFromQuery || (isSuperUser ? InvoiceTableTabs.SUPER_USER_CUSTOMER : InvoiceTableTabs.OUTSTANDING_INVOICES)
  )
  const [currentTablePage, setCurrentTablePage] = useState(1)
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null)
  const [isEntryModalOpen, setIsEntryModalOpen] = useState<boolean>(false)

  // Refs
  const tableRef = useRef<HTMLDivElement>(null)

  // Table data
  const { data: tableData, selectedRow, isLoading, isAlsoSupplier } = useInvoicesTable({ tab: activeTab, selectedDocumentId })

  useEffect(() => {
    const getSubNavTabs = () => {
      const tabs: Tab[] = [
        {
          label: t(($) => $.subNav.outstandingInvoices),
          mobileLabel: t(($) => $.subNav.outstandingInvoicesShort),
          path: InvoiceTableTabs.OUTSTANDING_INVOICES
        },
        {
          label: t(($) => $.subNav.allInvoices),
          mobileLabel: t(($) => $.subNav.allInvoicesShort),
          path: InvoiceTableTabs.ALL_INVOICES
        },
        { label: t(($) => $.subNav.allTransactions), path: InvoiceTableTabs.ALL_TRANSACTIONS }
      ]

      if (isSuperUser) {
        if (isAlsoSupplier) {
          tabs.unshift({ label: t(($) => $.subNav.superuserDistributor), path: InvoiceTableTabs.SUPER_USER_SUPPLIER })
        }

        tabs.unshift({
          label: t(($) => (isAlsoSupplier ? $.subNav.superuserClient : $.subNav.superuser)),
          mobileLabel: isAlsoSupplier ? undefined : t(($) => $.subNav.superuserShort),
          path: InvoiceTableTabs.SUPER_USER_CUSTOMER
        })
      }

      setTabs(tabs)
    }

    getSubNavTabs()
  }, [isAlsoSupplier, isSuperUser, t])

  /**
   * Handle page change
   * @param {number} page
   */
  const handlePageChange: PaginationProps['onPageChange'] = (page: number) => {
    tableRef?.current?.scrollIntoView({ behavior: 'instant' })
    setCurrentTablePage(page)
  }

  /**
   * Handle click tab
   * @param {InvoiceTableTabs} path
   */
  const handleClickTab = (path: InvoiceTableTabs) => {
    setActiveTab(path)
    setCurrentTablePage(1)
  }

  /**
   * Opens the entry modal for the clicked row
   * @param {string} id
   */
  const handleClickRow = (id: string) => {
    setSelectedDocumentId(id)
    setIsEntryModalOpen(true)
  }

  return (
    <div className={styles.container}>
      <InvoicesStatusBanner />

      <section className={styles.outlet}>
        {isTablet ? (
          <header
            className={classNames(styles.sticky, {
              [styles['hug-top']]:
                isProducer || (!Object.values(billingCyclesAccessRights.canAccess).some(Boolean) && !instalmentAccessRights.canAccess)
            })}
          >
            <Toggle<InvoiceTableTabs>
              className={styles.toggle}
              isFullwidth
              active={activeTab}
              options={tabs.map(({ path, mobileLabel, label }) => ({
                value: path as InvoiceTableTabs,
                label: mobileLabel || label
              }))}
              onClick={handleClickTab}
            />
          </header>
        ) : (
          <Card as="header" shadow={false} color="neutral" className={styles['switch-bar']}>
            <Toggle<InvoiceTableTabs>
              className={styles.toggle}
              active={activeTab}
              options={tabs.map(({ path, label }) => ({ value: path as InvoiceTableTabs, label }))}
              onClick={handleClickTab}
            />

            <AddressSwitcher className={styles.switcher} />
          </Card>
        )}

        {isLoading ? (
          <TableLoadingSkeleton />
        ) : tableData.length ? (
          <InvoicesTable
            ref={tableRef}
            onPageChange={handlePageChange}
            onClickRow={handleClickRow}
            {...{ tableData, activeTab, currentTablePage }}
          />
        ) : (
          <NoInvoices tab={activeTab} onClickNoInvoices={handleClickTab} />
        )}
      </section>

      {!accounts?.[selected.account]?.paymentDetails?.directDebit &&
        activeTab === InvoiceTableTabs.OUTSTANDING_INVOICES &&
        tableData.length > 0 && (
          <Banner title={t(($) => $.paymentMethodBanner.title)}>
            <p className="mb-100">{t(($) => $.paymentMethodBanner.text)}</p>
            <Link variant="tertiary" href={routes.USER_PAYMENT}>
              {t(($) => $.paymentMethodBanner.linkText)}
            </Link>
          </Banner>
        )}

      <InvoiceEntryModal entry={selectedRow?.entries ?? null} isOpen={isEntryModalOpen} onRequestClose={() => setIsEntryModalOpen(false)} />

      {feedbackWidgetData?.isActive && <FeedbackWidget id={feedbackWidgetData._id} openFeedbackWidget={openFeedbackWidget} />}
    </div>
  )
}

export default Invoices
